import { z } from 'zod';

export const teamIdSchema = z
  .union([z.literal('home'), z.literal('away'), z.literal('HOME'), z.literal('AWAY')])
  .transform((val) => (typeof val === 'string' ? val.toLowerCase() : val));
export const optionalTeamIdSchema = teamIdSchema.nullable().optional();

export const possessionStateEnum = z.enum([
  'HOME',
  'AWAY',
  'LOOSE',
  'SET_PIECE',
  'KICK_OFF',
  'GOAL_KICK',
  'THROW_IN',
  'CORNER',
  'FREE_KICK',
  'PENALTY'
]);

export const ballZoneEnum = z.enum([
  'GK',
  'DEF',
  'MID',
  'ATT',
  'WIDE_L',
  'WIDE_R'
]);

// Derived from scanning _add_structured_commentary_event calls and logic in game_engine.py
// This list should be considered extensive but might need additions if new event types are found.
export const commentaryEventTypeEnum = z.enum([
  'SAVE',
  // Current engine save outcomes
  'SAVE_TIPPED_BEHIND',
  'SAVE_SPILL',
  'SHOT_OFF_TARGET',
  'CROSS_SUCCESS',
  'HEADER_ATTEMPT',
  'CROSS_CLEARED',
  'CROSS_FAIL',
  'SHOT_BLOCKED',
  'CHANCE_CREATED',
  'DANGEROUS_ATTACK',
  'PRESSURE_BUILDING',
  'TACKLE_SUCCESS',
  'TACKLE_FAIL',
  'PASS_SUCCESS',
  'PASS_INTERCEPTED',
  'PASS_SETUP',        // NEW: For assisted shot setup
  'PASS_KEY',          // Enhanced key pass with narrative
  'DRIBBLE_SUCCESS',
  'DRIBBLE_FAIL_TACKLED',
  'SOLO_RUN_SETUP',    // NEW: For solo effort setup
  'SHOT_ATTEMPT',
  'SHOT_NARRATIVE',    // Rich shot with buildup
  'GOAL',
  'GOAL_NARRATIVE',    // Rich goal with full sequence
  'SCORE_UPDATE',
  'HALF_TIME',
  'FULL_TIME',
  'FOUL',
  'RED_CARD',
  'YELLOW_CARD',
  'FOUL_WARNING',
  'FREE_KICK_AWARDED',
  'PENALTY_AWARDED',
  'PENALTY_TAKEN',
  'PENALTY_GOAL',
  'PENALTY_SAVE',
  'PENALTY_MISS',
  'KICKOFF',
  'GOAL_KICK',
  'LONG_BALL',
  'OFFSIDE',
  'FREE_KICK', // Note: also a possession state
  'CORNER',    // Note: also a possession state
  'THROW_IN',  // Note: also a possession state
  'AERIAL_DUEL',
  'AERIAL_WON',
  'CLEARANCE',
  'HOLD_BALL',
  'INJURY',
  'SUBSTITUTION',
  'SYSTEM_MESSAGE',
  'POSITION_CHANGE',
  'TACTIC_CHANGE',
  'VAR_CHECK', 
  'VAR_GOAL_DISALLOWED',
  'VAR_GOAL_CONFIRMED', // Also add this if Python sends it
  'COLOR_COMMENTARY',   // For the color commentary events
  'FLOW_COMMENTARY'     // For flow-based commentary events
]);

export const simulationEndedReasonEnum = z.enum(['full_time', 'already_finished', 'error']);
export const simulationPausedReasonEnum = z.enum(['half_time', 'substitution', 'tactic_change', 'user_pause', 'injury']);
export const connectionIssueSeverityEnum = z.enum(['warning', 'error']);
export const commandTypeEnum = z.enum(['substitution', 'tactic_change']);
export const liveCommandAttemptIdSchema = z.string().trim().min(1).optional();

// Position types that support BOTH formats:
// 1. Simple format from engine state updates: GK, DF, DM, MF, AM, FW
// 2. Concatenated format for commands: GK, DFC, DFL, DFR, DMC, MFC, MFL, MFR, AMC, AML, AMR, FWC, FWL, FWR
export const playerPositionEnum = z.enum([
    // Simple format (what engine sends in state updates)
    'GK',   // Goalkeeper
    'DF',   // Defender (generic)
    'DM',   // Defensive Midfielder (generic)
    'MF',   // Midfielder (generic)
    'AM',   // Attacking Midfielder (generic)
    'FW',   // Forward (generic)
    
    // Concatenated format (what we send in commands and detailed schemas)
    'DFC', 'DFL', 'DFR',  // Defender Central/Left/Right
    'DMC',  // Defensive Midfielder Central
    'MFC', 'MFL', 'MFR',  // Midfielder Central/Left/Right
    'AMC', 'AML', 'AMR',  // Attacking Midfielder Central/Left/Right
    'FWC', 'FWL', 'FWR'   // Forward Central/Left/Right
]);

// TODO: Populate from teamsheet files or engine config if a definitive list exists.
// Tactics are data-driven.
export const teamTacticEnum = z.string().min(1); 

export const playerStatsSchema = z.object({
  games: z.number().int().optional(),
  minutes: z.number().int().optional(),
  saves: z.number().int().optional(),
  clean_sheets: z.number().int().optional(),
  penalties_saved: z.number().int().optional(),
  conceded: z.number().int().optional(),
  tackles: z.number().int().optional(),
  interceptions: z.number().int().optional(),
  aerial_duels_won: z.number().int().optional(),
  ball_recoveries: z.number().int().optional(),
  keypasses: z.number().int().optional(),
  progressive_passes: z.number().int().optional(),
  assists: z.number().int().optional(),
  crosses: z.number().int().optional(),
  shots: z.number().int().optional(),
  shots_on: z.number().int().optional(),
  shots_off: z.number().int().optional(),
  goals: z.number().int().optional(),
  successful_dribbles: z.number().int().optional(),
  penalties_scored: z.number().int().optional(),
  xg: z.number().optional(),
  xa: z.number().optional(),
  dp: z.number().int().optional(),
  injury: z.number().int().optional(),
  suspension: z.number().int().optional(),
  fitness: z.number().int().optional(),
  fouls: z.number().int().optional(),
  yellowcards: z.number().int().optional(),
  redcards: z.number().int().optional(),
  // Enhanced stats fields from new rating system
  bigChancesMissed: z.number().int().optional(),
  passAttempts: z.number().int().optional(),
  //progressivePasses: z.number().int().optional(), // DUPLICATE of progressive_passes above, but keep for compatibility
  dribbleAttempts: z.number().int().optional(),
  //successfulDribbles: z.number().int().optional(), // DUPLICATE of successful_dribbles above, but keep for compatibility
  progressiveCarries: z.number().int().optional(),
  tackleAttempts: z.number().int().optional(),
  blocks: z.number().int().optional(),
  clearances: z.number().int().optional(),
  errorsLeadingToShot: z.number().int().optional(),
  errorsLeadingToGoal: z.number().int().optional(),
  penaltiesConceded: z.number().int().optional(),
  goalsConceded: z.number().int().optional(),
  //penaltiesSaved: z.number().int().optional(), // DUPLICATE of penalties_saved above, but keep for compatibility
  psxg: z.number().optional(),
  //cleanSheet: z.boolean().optional(), // DUPLICATE of clean_sheets above, but different type (boolean vs int)
  fatigue: z.number().optional(),
  // Derived rate fields
  passCompletionRate: z.number().optional(),
  shotOnTargetRate: z.number().optional(),
  dribbleSuccessRate: z.number().optional(),
  tackleSuccessRate: z.number().optional(),
  savePercentage: z.number().optional(),
  matchAvgPassCompletion: z.number().optional(),
  // Rating fields
  rating: z.number().optional(),
  isRated: z.boolean().optional()
});

export const activePlayerSchema = z.object({
  name: z.string(),
  id: z.union([z.string(), z.number().int()]), // Assuming ID can be string or number from RosterPlayer
  position: playerPositionEnum,
  rating: z.number().optional(), // STATIC ability rating, not in-match performance
  stats: playerStatsSchema.optional(),
  is_home_team: z.boolean().optional(), // To distinguish if needed
  player_index: z.number().int().optional(), // Index in the team's active_players list
  // Optional live-match fields. Absent on older matches, so every consumer must
  // hide its widget rather than render a placeholder.
  fatigue: z.number().nullable().optional(), // 0-100, 100 = fresh
  live_rating: z.number().nullable().optional() // in-match performance rating
});

export const teamStatsSchema = z.object({
  shots_on_target: z.number().int().optional(),
  shots_off_target: z.number().int().optional(),
  fouls_committed: z.number().int().optional(),
  possession_percentage: z.number().optional(),
  // Add other relevant team stats from game_engine.py (e.g., team.final_shots_on etc.)
  final_shots_on: z.number().int().optional(),
  final_shots_off: z.number().int().optional(),
  final_shots_blocked: z.number().int().optional(),
  final_shots_total: z.number().int().optional(),
  final_fouls: z.number().int().optional(),
  yellow_cards: z.number().int().optional(),
  successful_dribbles: z.number().int().optional(),
  successful_crosses: z.number().int().optional(),
  successful_tackles: z.number().int().optional(),
  aerial_duels_won: z.number().int().optional(),
  progressive_passes: z.number().int().optional(),
  ball_recoveries: z.number().int().optional(),
  clean_sheet: z.boolean().optional(),
  xg_total: z.number().optional(),
  xa_total: z.number().optional(),
  substitutions_made: z.number().int().optional(),
  injuries_suffered: z.number().int().optional(),
  team_aggression: z.number().optional(),
  corners: z.number().int().optional()
}).passthrough();

// One entry per five in-game minutes. Optional: only newer engine builds emit it.
export const momentumBucketSchema = z.object({
  minute: z.number().int(),
  home: z.number(),
  away: z.number()
});

export const pressureStateSchema = z.object({
  home: z.number(),
  away: z.number()
});

export const teamStateSchema = z.object({
  id: teamIdSchema,
  name: z.string(),
  score: z.number().int(),
  tactic: teamTacticEnum,
  players: z.array(activePlayerSchema).optional(), // Full player list for the team
  stats: teamStatsSchema.optional()
});

// Extend commentary schema to support rich narrative
export const narrativeSchema = z.object({
  buildup: z.string().optional(),
  action: z.string().optional(), 
  outcome: z.string().optional(),
  aftermath: z.string().optional(),
  descriptors: z.array(z.string()).optional()
});

export const narrativeDataSchema = z.record(z.unknown());

function useTypeAsEventTypeFallback(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }

  if (Object.prototype.hasOwnProperty.call(raw, 'event_type')) {
    return raw;
  }

  const typeResult = commentaryEventTypeEnum.safeParse(raw.type);
  if (!typeResult.success) {
    return raw;
  }

  return {
    ...raw,
    event_type: typeResult.data
  };
}

export const individualCommentarySchema = z.preprocess(useTypeAsEventTypeFallback, z.object({
  event_type: commentaryEventTypeEnum.optional().default('SYSTEM_MESSAGE'),
  type: z.string().optional(),
  message_text: z.string().optional().default(''),
  minute: z.number().int().optional().default(0),
  formal_minute: z.string().optional().default('0'), // e.g., "45+2"
  team_context: optionalTeamIdSchema,
  player_name: z.string().nullable().optional(),
  secondary_player_name: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  emphasis: z.array(z.string()).optional(),
  narrative: narrativeSchema.optional(), // NEW: Rich narrative data
  narrative_data: narrativeDataSchema.nullable().optional(),
  sub_type: z.enum(['buildup', 'action', 'outcome', 'aftermath', 'descriptors']).optional(), // Phase 4: Sub-type for fanned-out events
  schema_version: z.string().default('1.0'), // Task 22: Schema versioning
  timestamp: z.string().optional(), // relaxed to plain string, RFC validation removed to accept backend format
  sequence_number: z.number().int().min(0).optional(), // Task 22: Event sequencing
  _fanout_key: z.string().optional(), // Internal key for fanned-out events
  unique_id: z.string().optional(), // React rendering key
  _correlationId: z.string().optional(),
  correlationId: z.string().optional(),
  score: z.object({
    home: z.number(),
    away: z.number()
  }).optional(), // Optional score for live updates
  // Optional shot-quality fields on shot-family events (GOAL, SAVE,
  // SHOT_OFF_TARGET, HEADER_ATTEMPT, PENALTY_*). Older matches omit them.
  xg: z.number().nullable().optional(),
  big_chance: z.boolean().optional(),
  // Ensure matchId survives schema parsing for strict page gating
  matchId: z.string().optional(),
  match_id: z.string().optional()
}));

export const gameStateSchema = z.object({
  minute: z.number().int(),
  formal_minute: z.string(), // e.g., "45+2" or "HT"
  second: z.number().int().optional(), // Optional, more granular time
  game_half: z.enum(["FIRST_HALF", "HALF_TIME", "SECOND_HALF", "EXTRA_TIME_FIRST_HALF", "EXTRA_TIME_SECOND_HALF", "PENALTY_SHOOTOUT", "FULL_TIME"]),
  possession: possessionStateEnum,
  possession_team_id: optionalTeamIdSchema,
  ball_zone: ballZoneEnum.nullable().optional(),
  player_with_ball: activePlayerSchema.nullable().optional(),
  home_team: teamStateSchema,
  away_team: teamStateSchema,
  commentary_events: z.array(individualCommentarySchema).optional(),
  // Optional momentum fields. The stats tab hides its momentum chart when the
  // engine does not send them, so old matches degrade cleanly.
  pressure_state: pressureStateSchema.optional(),
  momentum_buckets: z.array(momentumBucketSchema).optional(),
  // Added fields from engine state for completeness
  // Switched to z.unknown() instead of z.any() to work around Zod record bug
  last_action_outcome: z.record(z.unknown()).optional(), // Captures the dict from engine actions
  is_running: z.boolean().optional(),
  status: z.string().optional(),
  paused_state: z.string().nullable().optional(),
  is_paused_by_user: z.boolean().optional(),
  user_paused: z.boolean().optional(),
  isPaused: z.boolean().optional(),
  pauseReason: simulationPausedReasonEnum.nullable().optional(),
  simulation_speed: z.number().optional(), 
  message_log: z.array(z.string()).optional(), // General engine messages
  // Ensure matchId survives schema parsing for strict page gating
  matchId: z.string().optional(),
  // Keep backend attempt metadata from reconnect/load snapshots so live commands
  // can target the current engine boot attempt after validation strips unknowns.
  liveCommandAttemptId: liveCommandAttemptIdSchema,
  engineBootAttemptId: liveCommandAttemptIdSchema,
  engine_boot_attempt_id: liveCommandAttemptIdSchema
});

// --- Top-Level WebSocket Event Schemas ---

// Event: 'engine_state_update'
export const engineStateUpdateSchema = z.object({
  type: z.literal('engine_state_update'),
  payload: gameStateSchema
});

// Event: 'simulation_controls'
// No specific payload schema for confirmation, but requests will have one.

// Event: 'simulation_ended'
export const simulationEndedSchema = z.object({
  type: z.literal('simulation_ended'),
  payload: z.object({
    reason: simulationEndedReasonEnum,
    final_score: z.object({ home: z.number().int(), away: z.number().int() }),
    full_log: z.array(individualCommentarySchema).optional(), // or a more general log type
    home_team: z.object({
      score: z.number().int().min(0).optional(),
      stats: z.record(z.unknown()).optional()
    }).passthrough().optional(),
    away_team: z.object({
      score: z.number().int().min(0).optional(),
      stats: z.record(z.unknown()).optional()
    }).passthrough().optional(),
    summaryPlayerData: z.unknown().optional(),
    final_state: z.unknown().optional(),
    state: z.unknown().optional(),
    minute_of_first_error: z.union([z.number(), z.string()]).nullable().optional(),
    // Ensure matchId survives schema parsing for strict page gating
    matchId: z.string().optional()
  }).passthrough()
});

// Context attached to a simulation_paused event with reason 'injury'.
// team_context is the injured player's team, so it normalises to home/away the
// same way every other team reference on the wire does.
export const injuryPauseContextSchema = z.object({
  player_name: z.string(),
  team_context: teamIdSchema,
  position: z.string().nullable().optional(),
  minute: z.number().int().optional(),
  auto_resume_seconds: z.number().optional()
}).passthrough();

// Event: 'simulation_paused'
export const simulationPausedSchema = z.object({
  type: z.literal('simulation_paused'),
  payload: z.object({
    reason: simulationPausedReasonEnum,
    matchId: z.string(),
    current_minute: z.number().int().optional(),
    game_half: z.string().optional(), // Consider an enum if values are fixed
    // Only present when reason is 'injury'.
    injury: injuryPauseContextSchema.optional()
  })
});

// Event: 'simulation_resumed'
export const simulationResumedSchema = z.object({
  type: z.literal('simulation_resumed'),
  payload: z.object({
    matchId: z.string()
  })
});

// Event: 'commentary_update'
// BACKEND sends one commentary object per message.
export const commentaryUpdateSchema = z.object({
  type: z.literal('commentary_update'),
  payload: individualCommentarySchema
});

// Event: 'connection_issue' (from backend to frontend)
export const connectionIssueSchema = z.object({
    type: z.literal('connection_issue'),
    payload: z.object({
        message: z.string(),
        severity: connectionIssueSeverityEnum,
        matchId: z.string().optional(),
        match_id: z.string().optional(),
        durationMs: z.number().optional()
    }).passthrough()
});

// Event: 'error_message' (generic error from engine or backend)
export const errorMessageSchema = z.object({
    type: z.literal('error_message'),
    payload: z.object({
        message: z.string(),
        details: z.string().optional()
    })
});

// Event: 'state_saved_confirmation'
export const stateSavedConfirmationSchema = z.object({
    type: z.literal('state_saved_confirmation'),
    payload: z.object({
        message: z.string(),
        timestamp: z.string() // Or z.date()
    })
});

// Event: 'state_loaded_confirmation'
export const stateLoadedConfirmationSchema = z.object({
    type: z.literal('state_loaded_confirmation'),
    payload: z.object({
        message: z.string(),
        state_summary: z.any().optional() // Could be a light version of gameStateSchema
    })
});

// Event: 'live_command_update' (from backend to update UI on command status)
export const liveCommandUpdateSchema = z.object({
    type: z.literal('live_command_update'),
    payload: z.object({
        command_id: z.string(),
        status: z.enum(['RECEIVED', 'PROCESSING', 'ACCEPTED', 'REJECTED', 'COMPLETED', 'FAILED']),
        message: z.string().optional(),
        details: z.any().optional() // e.g., rejection reason, processed data
    })
});

// Event: 'match_preparing' (early engine boot indicator)
export const matchPreparingSchema = z.object({
  type: z.literal('match_preparing'),
  payload: z.object({
    matchId: z.string(),
    status: z.enum(['waitingForEngine', 'inProgress']),
    liveCommandAttemptId: liveCommandAttemptIdSchema
  })
});

// Event: 'match_auto_started' (cron auto-start, server transitioned scheduled→waitingForEngine)
export const matchAutoStartedSchema = z.object({
  type: z.literal('match_auto_started'),
  payload: z.object({
    matchId: z.string(),
    status: z.string(),
    liveCommandAttemptId: liveCommandAttemptIdSchema
  })
});

// Event: 'match_starting' (from backend to frontend when match is ready to begin or opponent is ready)
export const matchStartingSchema = z.object({
  type: z.literal('match_starting'),
  payload: z.object({
    matchId: z.string(),
    status: z.string(), // Or a more specific enum if applicable e.g., z.enum(['waitingForReady', 'inProgress'])
    liveCommandAttemptId: liveCommandAttemptIdSchema
  })
});

// Event: 'validation_error' (generic validation errors from backend)
export const validationErrorSchema = z.object({
  type: z.literal('validation_error'),
  payload: z.object({
    eventName: z.string(),
    errors: z.array(z.any()), // Backend sends Zod-formatted error objects
    timestamp: z.string()
  })
});

// Add Notification event schemas (transfer/match/etc.)
// -----------------------------------------------
// Generic payload for notifications emitted by backend (see Notification model virtuals)
export const notificationPayloadSchema = z.object({
  _id: z.string(),
  type: z.enum(['transfer', 'match', 'league', 'system']),
  recipient: z.string(),
  data: z.record(z.unknown()),
  read: z.boolean(),
  action: z.string().nullable(),
  leagueSlug: z.string().nullable().optional(),
  createdAt: z.string(),
  text: z.string().optional(),
  additionalAction: z.any().optional(),
  __v: z.number().int().optional(),
  // `id` is sometimes added by frontend when de-normalised
  id: z.string().optional()
});

// Event: 'notification:new' – pushed to recipient when a new notification is created
export const notificationNewSchema = z.object({
  type: z.literal('notification:new'),
  payload: notificationPayloadSchema
});
// -----------------------------------------------

// Event: 'busy_update' - Fired when engine changes busy state (timed commentary processing)
export const busyUpdateSchema = z.object({
  type: z.literal('busy_update'),
  payload: z.object({
    is_busy: z.boolean(),
    reason: z.string().optional() // Optional context like "processing_commentary"
  })
});

// Event: 'match_completed' - Fired when a match finishes for any reason
export const matchCompletedSchema = z.object({
  type: z.literal('match_completed'),
  payload: z.object({
    matchId: z.string(),
    status: z.enum(['completed', 'forfeited', 'cancelled']),
    homeScore: z.number().int(),
    awayScore: z.number().int()
  })
});

// Event: 'command_error' - Fired when engine is busy and rejects commands
export const commandErrorSchema = z.object({
  type: z.literal('command_error'),
  payload: z.object({
    command_type: z.string(),
    message: z.string(),
    reason: z.enum(['engine_busy', 'invalid_command', 'processing_error']),
    queue_length: z.number().int().optional()
  })
});

// Event: 'command_queued' - Fired when engine queues a command during busy periods
export const commandQueuedSchema = z.object({
  type: z.literal('command_queued'),
  payload: z.object({
    _correlationId: z.string().optional(),
    command_type: z.string().optional(),
    message: z.string().optional(),
    queue_length: z.number().int().optional(),
    team_id: teamIdSchema.optional(),
    out_player_identifier: z.union([z.string(), z.number().int()]).optional(),
    in_player_identifier: z.union([z.string(), z.number().int()]).optional(),
    out_player_wyId: z.number().int().positive().optional(),
    in_player_wyId: z.number().int().positive().optional(),
    new_position: playerPositionEnum.optional()
  }).passthrough()
});

// Event: 'command_result' - Fired when a queued/immediate command completes
export const commandResultSchema = z.object({
  type: z.literal('command_result'),
  payload: z.object({
    _correlationId: z.string().optional(),
    command_type: z.string().min(1),
    success: z.boolean(),
    message: z.string(),
    queued: z.boolean().optional(),
    // The team's absolute substitution total once this command has run.
    // Absent while a command is only queued, and from engines predating it.
    substitutions_made: z.number().int().optional(),
    team_id: teamIdSchema.optional(),
    out_player_identifier: z.union([z.string(), z.number().int()]).optional(),
    in_player_identifier: z.union([z.string(), z.number().int()]).optional(),
    out_player_wyId: z.number().int().positive().optional(),
    in_player_wyId: z.number().int().positive().optional(),
    new_position: playerPositionEnum.optional()
  }).passthrough()
});

// Helper for discriminating union
export const allIncomingEventSchemas = z.discriminatedUnion("type", [
  engineStateUpdateSchema,
  simulationEndedSchema,
  simulationPausedSchema,
  simulationResumedSchema, // <-- NEW: simulation resumed events
  commentaryUpdateSchema,
  connectionIssueSchema,
  errorMessageSchema,
  stateSavedConfirmationSchema,
  stateLoadedConfirmationSchema,
  liveCommandUpdateSchema,
  matchPreparingSchema,
  matchAutoStartedSchema,
  matchStartingSchema,
  validationErrorSchema,
  notificationNewSchema, // <-- NEW: allow notification:new events
  busyUpdateSchema, // <-- NEW: engine busy state updates
  matchCompletedSchema,
  commandErrorSchema, // <-- NEW: command error events when engine is busy
  commandQueuedSchema,
  commandResultSchema
]);

/**
 * Validates an incoming WebSocket event against the defined schemas.
 * @param {object} event - The event object to validate (event.type and event.payload).
 * @returns {{ success: boolean, data?: any, error?: ZodError }} Validation result.
 */
export const validateIncomingEvent = (eventToValidate) => {
  console.log("%%%% INSIDE validateIncomingEvent %%%%");
  console.log("eventToValidate being parsed:", JSON.stringify(eventToValidate, null, 2));

  // Assuming allIncomingEventSchemas is a Zod discriminatedUnion or similar 
  // that expects an object like { type: 'eventName', payload: { ... } }
  const validationResult = allIncomingEventSchemas.safeParse(eventToValidate);
  
  console.log(`Result of allIncomingEventSchemas.safeParse() for type ${eventToValidate.type}:`, validationResult.success);
  if (!validationResult.success) {
    console.error("Zod errors in validateIncomingEvent from allIncomingEventSchemas:", JSON.stringify(validationResult.error.format(), null, 2));
  }
  console.log("%%%% LEAVING validateIncomingEvent %%%%");
  return validationResult;
};

// --- Schemas for OUTGOING WebSocket Events (Frontend to Backend) ---

const requiredMatchIdSchema = z.preprocess((value) => {
  if (value === undefined || value === null) return value;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return value;
}, z
  .string()
  .trim()
  .min(1)
  .refine((matchId) => matchId !== 'undefined' && matchId !== 'null', { message: 'matchId required' }));

// For 'control_simulation' event
// Actions accepted by backend control_simulation route
export const simulationActionEnum = z.enum(['RESUME', 'PAUSE']);
export const controlSimulationPayloadSchema = z.object({
  action: simulationActionEnum,
  matchId: requiredMatchIdSchema,
  // Optional reason when action is PAUSE (e.g., 'substitution' | 'tactic_change')
  reason: simulationPausedReasonEnum.optional(),
  // Allow future expansion without breaking validation
}).passthrough();

// For 'user_command' event - Substitution
export const substitutionCommandPayloadSchema = z.object({
  command_type: z.literal('SUBSTITUTION'), // Keep for discriminated union if used directly elsewhere
  team_id: teamIdSchema, 
  out_player_identifier: z.union([z.string(), z.number().int()]),
  in_player_identifier: z.union([z.string(), z.number().int()]),
  new_position: playerPositionEnum
});

// For 'user_command' event - Tactic Change
export const tacticChangeCommandPayloadSchema = z.object({
  command_type: z.literal('TACTIC_CHANGE'), // Keep for discriminated union if used directly elsewhere
  team_id: teamIdSchema,
  new_tactic: teamTacticEnum, 
  formationId: z.string().optional()
});

// Schemas for the *data content* of command_data (WITHOUT the command_type discriminator inside them)
// Support both legacy snake_case and current camelCase payloads.
// Backend routing/validation requires matchId for in-progress engine commands.
const baseTacticChangeDataSchema = z.union([
  z
    .object({
      matchId: requiredMatchIdSchema,
      team_id: teamIdSchema,
      new_tactic: teamTacticEnum,
      formationId: z.string().optional()
    })
    .passthrough(),
  z
    .object({
      matchId: requiredMatchIdSchema,
      teamId: teamIdSchema,
      tacticId: z.coerce.string().min(1),
      formation: z.string().optional(),
      formationId: z.string().optional()
    })
    .passthrough()
]);

const baseSubstitutionDataSchema = z
  .object({
    matchId: requiredMatchIdSchema,
    team_id: teamIdSchema,
    out_player_identifier: z.union([z.string(), z.number().int()]),
    in_player_identifier: z.union([z.string(), z.number().int()]),
    new_position: playerPositionEnum,
    out_player_wyId: z.number().int().positive().optional(),
    in_player_wyId: z.number().int().positive().optional(),
    gk_emergency: z.boolean().optional()
  })
  .passthrough();

// Schema for the payload of 'send_engine_command'.
// Keep this aligned with backend/src/schemas/socketEventValidation.js.
export const outgoingUserCommandPayloadSchema = z.discriminatedUnion("command_type", [
  z.object({ command_type: z.literal('TACTIC_CHANGE'), command_data: baseTacticChangeDataSchema }),
  z.object({ command_type: z.literal('SUBSTITUTION'), command_data: baseSubstitutionDataSchema })
]);

// Historical shape only. The frontend must not emit save_game_state because the
// backend has no supported product listener for it.
export const saveGameStatePayloadSchema = z
  .object({
    matchId: z.string().min(1)
  })
  .passthrough();

// For 'load_game_state' event
// Must carry matchId so backend loads the correct saved state (and so validation keeps the field)
export const loadGameStatePayloadSchema = z
  .object({
    matchId: z.string().min(1)
  })
  .passthrough();

// For 'join_match' event
// Binds the current browser socket to the exact match stream it is viewing.
export const joinMatchPayloadSchema = z
  .object({
    matchId: z.string().min(1),
    clientStreamSeq: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).optional(),
    allowSessionTakeover: z.boolean().optional(),
    takeoverConfirmed: z.boolean().optional()
  })
  .passthrough();

// For 'leave_match' event
// Unbinds this browser socket from the match stream while keeping the app socket alive.
export const leaveMatchPayloadSchema = z
  .object({
    matchId: z.string().min(1)
  })
  .passthrough();

// --- Top-level OUTGOING event object schemas (similar to incoming ones) ---

export const outgoingControlSimulationSchema = z.object({
    type: z.literal('control_simulation'),
    payload: controlSimulationPayloadSchema
});

export const outgoingUserCommandSchema = z.object({
    type: z.literal('send_engine_command'), 
    payload: outgoingUserCommandPayloadSchema // This now refers to the new discriminated union for the payload
});

export const outgoingSaveGameStateSchema = z.object({
    type: z.literal('save_game_state'),
    payload: saveGameStatePayloadSchema
});

export const outgoingLoadGameStateSchema = z.object({
    type: z.literal('load_game_state'),
    payload: loadGameStatePayloadSchema
});

export const outgoingJoinMatchSchema = z.object({
    type: z.literal('join_match'),
    payload: joinMatchPayloadSchema
});

export const outgoingLeaveMatchSchema = z.object({
    type: z.literal('leave_match'),
    payload: leaveMatchPayloadSchema
});

// For 'request_commentary_resync' event
export const requestCommentaryResyncPayloadSchema = z.object({
  match_id: z.string().min(1),
  last_sequence: z.number().int().min(0).optional()
});

export const outgoingRequestCommentaryResyncSchema = z.object({
    type: z.literal('request_commentary_resync'),
    payload: requestCommentaryResyncPayloadSchema
});

// Helper for validating outgoing events before sending
export const allOutgoingEventSchemas = z.discriminatedUnion("type", [
    outgoingControlSimulationSchema,
    outgoingUserCommandSchema,
    outgoingLoadGameStateSchema,
    outgoingJoinMatchSchema,
    outgoingLeaveMatchSchema,
    outgoingRequestCommentaryResyncSchema
  ]);
  
/**
* Validates an outgoing WebSocket event object before sending.
* @param {object} event - The event object to validate (e.g., { type: 'control_simulation', payload: { action: 'PAUSE', matchId: 'match123' } }).
* @returns {{ success: boolean, data?: any, error?: ZodError }} Validation result.
*/
export const validateOutgoingEvent = (event) => {
   if (!event || typeof event.type !== 'string') {
       return { success: false, error: 'Outgoing event must have a type string.' };
   }
   return allOutgoingEventSchemas.safeParse(event);
}; 
